import { useMemo, useState } from 'react';
import { useClusterApi } from '../../api/ClusterApiProvider';
import { useAsync } from '../../api/useAsync';
import type { OfferedResource, Resource, ResourceKind } from '../../api/types';
import { useClusterSession } from '../../auth/ClusterSessionProvider';
import { mayManageCatalogue } from '../../auth/clusterRole';
import { useI18n } from '../../i18n/useI18n';
import { USING_STUBS } from '../../api';
import { StubCatalogueHint } from '../../stubs/StubBanner';
import { Button, Card, Loading, PageHead } from '../../ui/primitives';
import { FilterBar, SearchInput, SegmentedFilter } from '../../ui/form';
import { Alert, ErrorAlert } from '../../ui/Alert';
import { KIND_LABEL, ResourceTable } from '../../ui/ResourceTable';
import { IconPlus } from '../../ui/icons';
import { useToast } from '../../ui/Toast';
import { ResourceDialog } from './ResourceDialog';
import { ResourceDrawer } from './ResourceDrawer';
import { RemoveResourceDialog } from './RemoveResourceDialog';
import { ScopesCard } from './ScopesCard';
import type { WriteOutcome } from './catalogWrite';
import { kindsOffered } from './resourceForm';

type Filter = ResourceKind | 'all';

/** Stable identity so the memo below is not invalidated every render. */
const NO_RESOURCES: Resource[] = [];

type Dialog =
  | { kind: 'new' }
  /** The panel: one row, whole, beside the list it came from. */
  | { kind: 'open'; resource: Resource }
  | { kind: 'edit'; resource: Resource }
  | { kind: 'remove'; resource: Resource };

/** The optional prop, spread only when there is something to spread. */
function offerProp(offer: OfferedResource | undefined): { offer?: OfferedResource } {
  return offer === undefined ? {} : { offer };
}

/** What the last write amounted to, kept only long enough to be read. */
interface LastWrite {
  id: string;
  removed: boolean;
  outcome: WriteOutcome;
}

/**
 * The catalogue of one cluster: what it serves, and where it is changed.
 *
 * **It is the declared catalogue.** These rows are what the control plane will
 * make true — a write lands here first and is carried to the cluster second, so
 * for a moment (and longer, if the cluster was not reachable) the two differ.
 * How far behind the cluster is shows on the tenancy's card in the account.
 *
 * **Writing lives here and only here.** It is a change inside one cluster, made
 * by somebody that cluster's own identity provider vouched for, and it carries
 * their name to the tenancy's own audit trail. Nobody in the account carries it:
 * the routes that wrote a catalogue from there were removed rather than
 * duplicated, and two doors to one operation are two doors to guard.
 *
 * The controls are shown to `cluster_admin` and to nobody else. Hiding is all
 * that does — the server checks the group again, on every call, as the policy
 * stands at that moment.
 *
 * Note what is missing: nothing here names a tenancy. `listResources` has no
 * parameter for one, the address has no segment for one, and which cluster this
 * is was settled by the host the page came from.
 */
export function CatalogScreen() {
  const { t, plural } = useI18n();
  const api = useClusterApi();
  const { session } = useClusterSession();
  const toast = useToast();

  const mayWrite = mayManageCatalogue(session);
  const actorName = session?.actor.subject ?? '';
  // Which kinds this tenancy's own build understands — the server's answer, kept
  // to what this console has a word and a picture for. Both the declaration form
  // and the filter above the table are built from it.
  const offeredKinds = useMemo(() => kindsOffered(session), [session]);

  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [lastWrite, setLastWrite] = useState<LastWrite | null>(null);

  const state = useAsync((signal) => api.listResources(signal), 'resources');
  // Who answers for each declared id. Read beside the catalogue rather than
  // folded into it: they are two different facts from two different places —
  // what somebody declared, and what a connector says it is serving — and a
  // resource that has one without the other is exactly what the screen has to
  // show.
  const offers = useAsync((signal) => api.listOffered(signal), 'offered');
  // The gateway's routing table. Its own call because it is its own fact: what
  // is declared, what a connector once said, and what has a tunnel behind it
  // now are three different things, and only the third is liveness.
  const live = useAsync((signal) => api.listServing(signal), 'serving');
  const resources: Resource[] = state.status === 'ready' ? state.value : NO_RESOURCES;

  const offered = offers.status === 'ready' ? offers.value : undefined;
  const serving = live.status === 'ready' ? live.value : undefined;
  const offerFor = (resource: Resource) =>
    offered?.find((offer) => offer.resource_id === (resource.id ?? resource.hostname));

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return resources.filter((resource) => {
      if (filter !== 'all' && resource.kind !== filter) return false;
      if (needle === '') return true;
      const labels = Object.entries(resource.labels)
        .map(([key, value]) => `${key}=${value}`)
        .join(' ');
      const names = [resource.id, resource.hostname, ...(resource.aliases ?? [])]
        .filter((one): one is string => one !== undefined)
        .join(' ');
      return `${names} ${resource.project} ${resource.environment} ${labels}`
        .toLowerCase()
        .includes(needle);
    });
  }, [resources, filter, query]);

  /**
   * A write that reached the cluster is a confirmation and gone; a write that
   * only got as far as the declaration stays on screen, because it is a thing
   * somebody has to know about the cluster rather than about their click.
   */
  function settled(id: string, outcome: WriteOutcome, removed: boolean) {
    setDialog(null);
    setLastWrite({ id, removed, outcome });
    state.reload();
    if (outcome.done === 'delivered') {
      toast(t(removed ? 'toast.resourceRemoved' : 'toast.resourceDeclared', { id }));
    }
  }

  return (
    <div className="content-inner">
      <PageHead
        title={t('catalog.title')}
        actions={
          mayWrite ? (
            <Button
              variant="primary"
              onClick={() => {
                setDialog({ kind: 'new' });
              }}
            >
              <IconPlus size={16} />
              {t('catalog.declare')}
            </Button>
          ) : undefined
        }
      />

      {/* The result of a write that reached the declaration and not the
          cluster. A state that is wrong now, and the only band this screen
          has: what a declared catalogue is stands under the table, once. */}
      {lastWrite !== null && lastWrite.outcome.done === 'declared' && (
        <div style={{ marginBottom: 'var(--gap)' }}>
          <Alert tone="warn" title={t('catalog.notTold.title')} live>
            <p>
              {t(lastWrite.removed ? 'catalog.notTold.removed' : 'catalog.notTold.written', {
                id: lastWrite.id,
              })}
            </p>
          </Alert>
        </div>
      )}

      {state.status === 'error' && (
        <ErrorAlert
          error={state.error}
          action={
            <div style={{ marginTop: 12 }}>
              <Button size="sm" onClick={state.reload}>
                {t('common.retry')}
              </Button>
            </div>
          }
        />
      )}

      {state.status === 'loading' && <Loading />}

      {state.status === 'ready' && (
        <>
          {/* The counts are the server's list, counted: what a kind holds is
              the reason somebody presses its button, and it saves a row of
              tiles nobody could fill from this answer. */}
          <FilterBar
            filters={
              <SegmentedFilter<Filter>
                legend={t('catalog.filter.label')}
                value={filter}
                onChange={setFilter}
                options={[
                  { value: 'all', label: t('catalog.filter.all'), count: resources.length },
                  // The same list the declaration form offers, and for the same
                  // reason: a chip for a kind this tenancy's build cannot serve
                  // is a permanent zero that narrows nothing.
                  ...offeredKinds.map((kind) => ({
                    value: kind,
                    label: t(KIND_LABEL[kind]),
                    count: resources.filter((resource) => resource.kind === kind).length,
                  })),
                ]}
              />
            }
            search={
              <SearchInput
                label={t('catalog.search.label')}
                placeholder={t('catalog.search.placeholder')}
                value={query}
                onChange={setQuery}
              />
            }
          />

          <Card>
            <div className="card-head">
              <span className="ct">{t('catalog.declared.title')}</span>
              <span className="ca faint">{plural('catalog.count', visible.length)}</span>
            </div>
            <ResourceTable
              resources={visible}
              total={resources.length}
              {...(offered === undefined ? {} : { offered })}
              {...(serving === undefined ? {} : { serving })}
              onOpen={(resource) => {
                setDialog({ kind: 'open', resource });
              }}
              empty={{
                title: t('catalog.none.title'),
                body: t(mayWrite ? 'catalog.none.declare' : 'catalog.none.body'),
              }}
            />
          </Card>

          {/* Under the catalogue rather than beside it: a boundary is read
              after the rows it explains, and a tenancy with none should not
              meet it first. */}
          <div style={{ marginTop: 'var(--gap)' }}>
            <ScopesCard mayWrite={mayWrite} kinds={offeredKinds} />
          </div>

          {/* Said once, under the rows: what a declared catalogue is, and where
              it is written. It was a band at the top of every visit. */}
        </>
      )}

      {USING_STUBS && mayWrite && (
        <div style={{ marginTop: 'var(--gap)' }}>
          <StubCatalogueHint onChanged={state.reload} />
        </div>
      )}

      {dialog?.kind === 'new' && (
        <ResourceDialog
          kinds={offeredKinds}
          actorName={actorName}
          onClose={() => {
            setDialog(null);
          }}
          onWritten={(id, outcome) => {
            settled(id, outcome, false);
          }}
        />
      )}

      {dialog?.kind === 'edit' && (
        <ResourceDialog
          surface="drawer"
          resource={dialog.resource}
          kinds={offeredKinds}
          actorName={actorName}
          onClose={() => {
            // Back to the panel it was opened from, not to the bare list: the
            // person was reading a row and is still reading it.
            setDialog({ kind: 'open', resource: dialog.resource });
          }}
          onWritten={(id, outcome) => {
            settled(id, outcome, false);
          }}
        />
      )}

      {dialog?.kind === 'open' && (
        <ResourceDrawer
          resource={dialog.resource}
          {...offerProp(offerFor(dialog.resource))}
          {...(serving === undefined
            ? {}
            : { serving: serving.includes(dialog.resource.id ?? '') })}
          mayWrite={mayWrite}
          onEdit={() => {
            setDialog({ kind: 'edit', resource: dialog.resource });
          }}
          onRemove={() => {
            setDialog({ kind: 'remove', resource: dialog.resource });
          }}
          onClose={() => {
            setDialog(null);
          }}
        />
      )}

      {dialog?.kind === 'remove' && (
        <RemoveResourceDialog
          resource={dialog.resource}
          actorName={actorName}
          onClose={() => {
            setDialog(null);
          }}
          onRemoved={(id, outcome) => {
            settled(id, outcome, true);
          }}
        />
      )}
    </div>
  );
}
